import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface PromoCode {
  promoCode: string;
  description?: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxDiscount?: number;
  minBookingAmount?: number;
  maxUsage?: number;
  currentUsage: number;
  expiryDate?: string;
  startDate?: string;
  applicableServices?: string[];
  zipCodes?: string[];
  status: 'active' | 'inactive' | 'expired';
  createdAt: string;
}

export interface PromoCodeUsage {
  usageId: string;
  promoCode: string;
  bookingId: string;
  customerId: string;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  usedAt: string;
}

export interface PromoCodeUsageStats {
  promoCode: PromoCode;
  usageCount: number;
  totalDiscounts: number;
  uniqueUsers: number;
  usages: PromoCodeUsage[];
}

export interface PromoCodeListResponse {
  promoCodes: PromoCode[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListPromoCodesParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  status?: string;
}

class PromoCodeManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listPromoCodes(params?: ListPromoCodesParams): Promise<PromoCodeListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);

    const response = await this.api.get<{ data: PromoCodeListResponse }>(
      `/admin/promo-codes?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async createPromoCode(promoCode: Omit<PromoCode, 'currentUsage' | 'status' | 'createdAt'>): Promise<PromoCode> {
    const response = await this.api.post<{ data: { promoCode: PromoCode } }>('/admin/promo-codes', promoCode);
    return response.data.data.promoCode;
  }

  async updatePromoCode(promoCodeId: string, updates: Partial<PromoCode>): Promise<PromoCode> {
    const response = await this.api.put<{ data: { promoCode: PromoCode } }>(
      `/admin/promo-codes/${promoCodeId}`,
      updates
    );
    return response.data.data.promoCode;
  }

  async deletePromoCode(promoCodeId: string): Promise<PromoCode> {
    const response = await this.api.delete<{ data: { promoCode: PromoCode } }>(
      `/admin/promo-codes/${promoCodeId}`
    );
    return response.data.data.promoCode;
  }

  async getPromoCodeUsage(promoCodeId: string): Promise<PromoCodeUsageStats> {
    const response = await this.api.get<{ data: PromoCodeUsageStats }>(
      `/admin/promo-codes/${promoCodeId}/usage`
    );
    return response.data.data;
  }
}

export const promoCodeManagementService = new PromoCodeManagementService();

